import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { CompanyCenterCreateRequestDTO, CompanyCenterDTO, CompanyCenterUpdateRequestDTO, FindAllCompanyCentersResponseDTO, FindAllFiltersCompanyCentersDTO } from "./dto";
import { NewCompanyCenterService } from "./service";

interface CompanyCenterState {
    companyCenterCreateRequest: CompanyCenterCreateRequestDTO;
    companyCenterUpdateRequest: CompanyCenterUpdateRequestDTO;
    companyCenterCreateErrors: Partial<Record<keyof CompanyCenterCreateRequestDTO, string>>
    findAllCompanyCentersResponse?: FindAllCompanyCentersResponseDTO
    findAllCompanyCenterStatus: PromiseStatuses;
    companyCenterCreateStatus: PromiseStatuses
    companyCenterUpdateStatus: PromiseStatuses
    companyCenterByIdStatus: PromiseStatuses
    companyCenterByIdResponse?: CompanyCenterDTO
    companyCenterByIdsStatus: PromiseStatuses
    companyCenterByIdsResponse?: CompanyCenterDTO[]
    deleteCompanyCenterStatus: PromiseStatuses
    removeCompanyCenterStatus: PromiseStatuses
}

const initialState: CompanyCenterState = {
    companyCenterCreateRequest: {
        countryRegion: "",
        province: "",
        city: "",
        zipCode: "",
        address: "",
        latitude: null,
        longitude: null,
        referrer: null,
        phone: "",
        email: "",
        pec: "",
        activationDate: null,
        deactivationDate: null,
        active: false,
        description: "",
        customerId: "",
        code: ""
    },
    companyCenterUpdateRequest: {
        countryRegion: "",
        province: "",
        city: "",
        zipCode: "",
        address: "",
        latitude: null,
        longitude: null,
        referrer: null,
        phone: "",
        email: "",
        pec: "",
        activationDate: undefined,
        deactivationDate: undefined,
        active: false,
        description: "",
        code: ""
    },
    findAllCompanyCenterStatus: "idle",
    companyCenterCreateStatus: "idle",
    companyCenterUpdateStatus: "idle",
    companyCenterByIdStatus: "idle",
    deleteCompanyCenterStatus: "idle",
    companyCenterByIdsStatus: "idle",
    companyCenterCreateErrors: {},
    removeCompanyCenterStatus: "idle"
}

export const validateCompanyCenterCreateRequest = createAsyncThunk(
    'companyCenter/validateCreateRequest',
    async (request: CompanyCenterCreateRequestDTO, thunkApi): Promise<void> => {
        const errors: Partial<Record<keyof CompanyCenterCreateRequestDTO, string>> = {}

        const requiredStrings: { field: keyof CompanyCenterCreateRequestDTO; label: string }[] = [
            { field: 'code', label: 'ID / Codice' },
            { field: 'description', label: 'Descrizione' },
            { field: 'address', label: 'Indirizzo' },
            { field: 'countryRegion', label: 'Regione' },
            { field: 'province', label: 'Provincia' },
            { field: 'city', label: 'Comune' },
            { field: 'zipCode', label: 'CAP' },
            { field: 'customerId', label: 'Cliente' },
        ]

        for (const { field, label } of requiredStrings) {
            const value = request[field]
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                errors[field] = `${label} è obbligatorio`
            }
        }

        if (!request.activationDate) {
            errors.activationDate = 'Data Ingresso è obbligatoria'
        }

        if (Object.keys(errors).length > 0) {
            return thunkApi.rejectWithValue(errors) as never
        }
    }
)

export const createCompanyCenter = createAsyncThunk(
    'companyCenter/createCompanyCenter',
    async (request: CompanyCenterCreateRequestDTO, thunkApi): Promise<number> => {
        const companyCenterService = NewCompanyCenterService()

        return companyCenterService.createCompanyCenter(request).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const updateCompanyCenter = createAsyncThunk(
    'companyCenter/updateCompanyCenter',
    async (request: { id: string, data: CompanyCenterUpdateRequestDTO }, thunkApi): Promise<void> => {
        const companyCenterService = NewCompanyCenterService()

        return companyCenterService.updateCompanyCenter(request.id, request.data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

export const getCompanyCenters = createAsyncThunk(
    'companyCenter/getCompanyCenters',
    async (filters: FindAllFiltersCompanyCentersDTO, thunkApi): Promise<FindAllCompanyCentersResponseDTO> => {
        const companyCenterService = NewCompanyCenterService()

        return companyCenterService.getCompanyCenters(filters)
    },
)

export const getCompanyCenterById = createAsyncThunk(
    'companyCenter/getCompanyCenterById',
    async (id: string, thunkApi): Promise<CompanyCenterDTO> => {
        const companyCenterService = NewCompanyCenterService()

        return companyCenterService.getCompanyCenterById(id)
    },
)

export const getCompanyCenterByIds = createAsyncThunk(
    'companyCenter/getCompanyCenterByIds',
    async (request: string[], thunkApi): Promise<CompanyCenterDTO[]> => {
        const companyCenterService = NewCompanyCenterService()

        const promises: Promise<CompanyCenterDTO>[] = request.map((id) =>

            companyCenterService.getCompanyCenterById(id)
        )
        return Promise.all(promises)
    }
)

export const deleteCompanyCenter = createAsyncThunk(
    'companyCenter/deleteCompanyCenter',
    async (id: string, thunkApi): Promise<void> => {
        const companyCenterService = NewCompanyCenterService()

        return companyCenterService.deleteCompanyCenter(id)
    },
)

export const removeCompanyCenter = createAsyncThunk(
    'companyCenter/removeCompanyCenter',
    async (id: string, thunkApi): Promise<void> => {
        const companyCenterService = NewCompanyCenterService()

        return companyCenterService.removeCompanyCenter(id)
    },
)

const companyCenterSlice = createSlice({
    name: 'companyCenter/slice',
    initialState,
    reducers: {
        setCompanyCenterCreateRequestField: (state, action: { payload: { field: keyof CompanyCenterCreateRequestDTO, value: any } }) => {
            state.companyCenterCreateRequest = {
                ...state.companyCenterCreateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        setCompanyCenterUpdateRequestField: (state, action: { payload: { field: keyof CompanyCenterUpdateRequestDTO, value: any } }) => {
            state.companyCenterUpdateRequest = {
                ...state.companyCenterUpdateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        setCompanyCenterUpdateRequest: (state, action) => {
            state.companyCenterUpdateRequest = action.payload
        },
        setCompanyCenterCreateStatus: (state, action) => {
            state.companyCenterCreateStatus = action.payload
        },
        setCompanyCenterUpdateStatus: (state, action) => {
            state.companyCenterUpdateStatus = action.payload
        },
        setDeleteCompanyCenterStatus: (state, action) => {
            state.deleteCompanyCenterStatus = action.payload
        },
        resetCompanyCenterCreateRequest: (state) => {
            state.companyCenterCreateRequest = initialState.companyCenterCreateRequest
        },
        resetCompanyCenterUpdateRequest: (state) => {
            state.companyCenterUpdateRequest = initialState.companyCenterUpdateRequest
        },
        setCompanyCenterCreateErrors: (state, action: { payload: Partial<Record<keyof CompanyCenterCreateRequestDTO, string>> }) => {
            state.companyCenterCreateErrors = action.payload
        },
        resetCompanyCenterCreateErrors: (state) => {
            state.companyCenterCreateErrors = {}
        },
        resetCompanyCenterCreateError: (state, action: { payload: keyof CompanyCenterCreateRequestDTO }) => {
            delete state.companyCenterCreateErrors[action.payload]
        },
        resetCompanyCenterByIds: (state) => {
            state.companyCenterByIdsResponse = undefined
            state.companyCenterByIdsStatus = 'idle'
        },
        setRemoveCompanyCenterStatus: (state, action) => {
            state.removeCompanyCenterStatus = action.payload
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getCompanyCenters.pending, (state) => {
                state.findAllCompanyCenterStatus = 'loading'
            })
            .addCase(getCompanyCenters.fulfilled, (state, action) => {
                state.findAllCompanyCenterStatus = 'successfully'
                state.findAllCompanyCentersResponse = action.payload
            })
            .addCase(getCompanyCenters.rejected, (state) => {
                state.findAllCompanyCenterStatus = 'failed'
            })
            .addCase(getCompanyCenterById.pending, (state) => {
                state.companyCenterByIdStatus = 'loading'
            })
            .addCase(getCompanyCenterById.fulfilled, (state, action) => {
                state.companyCenterByIdStatus = 'successfully'
                state.companyCenterByIdResponse = action.payload
            })
            .addCase(getCompanyCenterById.rejected, (state) => {
                state.companyCenterByIdStatus = 'failed'
            })
            .addCase(getCompanyCenterByIds.pending, (state) => {
                state.companyCenterByIdsStatus = 'loading'
            })
            .addCase(getCompanyCenterByIds.fulfilled, (state, action) => {
                state.companyCenterByIdsStatus = 'successfully'
                state.companyCenterByIdsResponse = action.payload
            })
            .addCase(getCompanyCenterByIds.rejected, (state) => {
                state.companyCenterByIdsStatus = 'failed'
            })

            .addCase(createCompanyCenter.pending, (state) => {
                state.companyCenterCreateStatus = 'loading'
            })
            .addCase(createCompanyCenter.fulfilled, (state, action) => {
                state.companyCenterCreateStatus = 'successfully'
            })
            .addCase(createCompanyCenter.rejected, (state) => {
                state.companyCenterCreateStatus = 'failed'
            })
            .addCase(updateCompanyCenter.pending, (state) => {
                state.companyCenterUpdateStatus = 'loading'
            })
            .addCase(updateCompanyCenter.fulfilled, (state, action) => {
                state.companyCenterUpdateStatus = 'successfully'
            })
            .addCase(updateCompanyCenter.rejected, (state) => {
                state.companyCenterUpdateStatus = 'failed'
            })
            .addCase(deleteCompanyCenter.pending, (state) => {
                state.deleteCompanyCenterStatus = 'loading'
            })
            .addCase(deleteCompanyCenter.fulfilled, (state, action) => {
                state.deleteCompanyCenterStatus = 'successfully'
                state.companyCenterByIdsResponse = state.companyCenterByIdsResponse?.filter(
                    (center) => center.id !== action.meta.arg
                )
            })
            .addCase(deleteCompanyCenter.rejected, (state) => {
                state.deleteCompanyCenterStatus = 'failed'
            })
            .addCase(validateCompanyCenterCreateRequest.fulfilled, (state) => {
                state.companyCenterCreateErrors = {}
            })
            .addCase(validateCompanyCenterCreateRequest.rejected, (state, action) => {
                state.companyCenterCreateErrors = action.payload ?? {}
            })
            .addCase(removeCompanyCenter.pending, (state) => {
                state.removeCompanyCenterStatus = 'loading'
            })
            .addCase(removeCompanyCenter.fulfilled, (state, action) => {
                state.removeCompanyCenterStatus = 'successfully'
            })
            .addCase(removeCompanyCenter.rejected, (state) => {
                state.removeCompanyCenterStatus = 'failed'
            })
    },
})

export const {
    setCompanyCenterCreateRequestField,
    setCompanyCenterUpdateRequestField,
    setCompanyCenterUpdateRequest,
    setCompanyCenterCreateStatus,
    setCompanyCenterUpdateStatus,
    setDeleteCompanyCenterStatus,
    resetCompanyCenterCreateRequest,
    resetCompanyCenterUpdateRequest,
    setCompanyCenterCreateErrors,
    resetCompanyCenterCreateErrors,
    resetCompanyCenterCreateError,
    resetCompanyCenterByIds,
    setRemoveCompanyCenterStatus
} = companyCenterSlice.actions

export default companyCenterSlice.reducer